<template>
  <q-dialog v-model="isVisible" persistent class="notice-dialog">

    <div v-if="networkNotice" class="network-notice-container">
      <div class="network-notice-title flex items-center justify-center gap-[4px] mb-[5px]">
        <img class="w-[18px] h-[18px]" :src="imageBase64.lightning" alt="">
        <span>{{ mainTitleComputed }}</span>
      </div>
      <div class="network-notice-main">
        <span>{{ mainTextComputed }}</span>
        <span>{{ displaySubText }}</span>
      </div>
    </div>

    <div v-else class="notice-dialog-container">
      <div class="notice-title-content">
        <!-- 蓝色背景图 -->
        <img :src="bgImage" class="blue-bg" alt="">
        <!-- 标题 -->
        <div class="notice-title">{{ displayTitleComputed }}</div>
        <!-- 狗狗图片 -->
        <img :src="dogImage" alt="dog" class="dog-image" />
        <img :src="oopsImage" alt="oops" class="oops-image" />
      </div>

      <!-- 弹窗主体 -->
      <div class="notice-dialog-content" :style="themeStyle">

        <!-- 主要文字 -->
        <div class="notice-main-text" :style="mainTextStyle" v-html="mainTextComputed"></div>
        <!-- 副标题（带礼物图标） -->
        <div class="notice-sub-text" v-if="displaySubText">
          <img v-if="giftIcon" src="@/assets/imgs/gift-blue.webp" alt="gift" class="gift-icon" />
          <span>{{ displaySubText }}</span>
        </div>

        <!-- 确认按钮 -->
        <!-- <ButtonThe3DButton class="notice-button" color="blue" @click="handleConfirm">
          {{ displayButtonText }}
        </ButtonThe3DButton> -->

        <!-- 确认与取消按钮 -->
        <div class="flex items-center justify-center">
          <ButtonThe3DButton v-if="displayButtonText" class="notice-buttons flex-1" :color="confirmColor" @click="handleConfirm">
            {{ displayButtonText }}
          </ButtonThe3DButton>
          <ButtonThe3DButton v-if="cancelButtonText" class="notice-buttons flex-1" color="red" @click="handleCancel">
            {{ cancelButtonText }}
          </ButtonThe3DButton>
        </div>
      </div>
    </div>
  </q-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import imageBase64 from './imageBase64.js'

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  networkNotice: {
    type: Boolean,
    default: false
  },
  displayTitle: {
    type: String,
    default: ''
  },
  mainTitle:{
    type: String,
    default: ''
  },
  mainText: {
    type: String,
    default: ''
  },
  mainTextStyle: {
    type: String,
    default: ''
  },
  displaySubText: {
    type: String,
    default: ''
  },
  displayButtonText: {
    type: String,
    default: ''
  },
  cancelButtonText: {
    type: String,
    default: ''
  },
  giftIcon: {
    type: Boolean,
    default: true
  },
  confirmColor:{
    type: String,
    default: 'blue'
  },
  dogImage: {
    type: String,
    default: () => new URL('@/assets/imgs/npc.webp', import.meta.url).href
  },
  bgImage: {
    type: String,
    default: () => new URL('@/assets/imgs/blue-bg.webp', import.meta.url).href
  },
  oopsImage: {
    type: String,
    default: () => new URL('@/assets/imgs/oops.webp', import.meta.url).href
  },
  themeStyle: {
    type: String,
    default: ''
  },
})

// Emits
const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

// 🔄 内部响应式数据（用于支持动态更新）
const internalDisplayTitle = ref(props.displayTitle)
const internalMainTitle = ref(props.mainTitle)
const internalMainText = ref(props.mainText)

// 监听 props 变化并同步到内部数据
watch(() => props.displayTitle, (newVal) => { internalDisplayTitle.value = newVal })
watch(() => props.mainTitle, (newVal) => { internalMainTitle.value = newVal })
watch(() => props.mainText, (newVal) => { internalMainText.value = newVal })

// Computed
const isVisible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

// Computed for display values
const displayTitleComputed = computed(() => internalDisplayTitle.value || props.displayTitle)
const mainTitleComputed = computed(() => internalMainTitle.value || props.mainTitle)
const mainTextComputed = computed(() => internalMainText.value || props.mainText)

// Methods
const handleConfirm = () => {
  emit('confirm')
  isVisible.value = false
}

const handleCancel = () => {
  emit('cancel')
  isVisible.value = false
}

// 🎯 暴露更新方法供外部调用
const updateContent = (updates) => {
  if (updates.displayTitle !== undefined) internalDisplayTitle.value = updates.displayTitle
  if (updates.mainTitle !== undefined) internalMainTitle.value = updates.mainTitle
  if (updates.mainText !== undefined) internalMainText.value = updates.mainText
}

// 暴露方法
defineExpose({
  handleConfirm,
  handleCancel,
  updateContent
})
</script>

<style lang="scss">
// 全局样式，针对 notice-dialog class 的 q-dialog
.notice-dialog .q-dialog__inner {
  max-width: 500px !important;
  margin: 0 auto;
}
</style>

<style lang="scss" scoped>
.notice-dialog-container {
  position: relative;
  max-width: 243px !important;
  overflow: inherit;

  .notice-title-content {
    position: relative;

    // 标题
    .notice-title {
      color: #FFF;
      font-size: 20px;
      font-weight: 600;
      line-height: 18px;
      position: absolute;
      left: 13px;
      top: 15px;
    }

    .blue-bg {
      width: 100%;
      height: auto;
    }

    // 狗狗图片区域
    .dog-image {
      width: 86px;
      height: auto;
      transform: rotate(2.447deg);
      flex-shrink: 0;
      aspect-ratio: 43/56;
      object-fit: contain;
      position: absolute;
      right: 0px;
      bottom: 0px;
    }

    .oops-image {
      position: absolute;
      bottom: 25px;
      right: 76px;
      width: 61px;
      height: auto;
      transform: rotate(-16.65deg);
      flex-shrink: 0;
      aspect-ratio: 61.77/49.28;
      object-fit: contain;
      animation: float 2s ease-in-out infinite;
    }
  }
}

// 弹窗主体
.notice-dialog-content {
  background: #101B27;
  border-radius: 16px;
  padding: 8px 11px 8px 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  border: 2px solid #07F;
  position: relative;
  overflow: hidden;
  margin-top: -21px;

}

// 主要文字
.notice-main-text {
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  margin-bottom: 10px;
  line-height: 1.5;
  position: relative;
  z-index: 1;
}

// 副标题
.notice-sub-text {
  display: flex;
  gap: 6px;
  color: #E5E5E5;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 13px;

  /* 166.667% */
  .gift-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    aspect-ratio: 1/1;
    animation: bounce 1s ease-in-out infinite;
  }
}

// 确认按钮
.notice-button {
  width: 100%;
  height: 33px;
  border-radius: 8px;
}

// 动画
@keyframes float {

  0%,
  100% {
    transform: translateY(0) rotate(-5deg);
  }

  50% {
    transform: translateY(-10px) rotate(-5deg);
  }
}

@keyframes bounce {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.2);
  }
}

// 网络通知样式
.network-notice-container {
  max-width: 243px !important;
  overflow: inherit;
  background:
    linear-gradient(#101B27, #101B27) padding-box,
    linear-gradient(to bottom, #F77B00, #D9804C) border-box;
  border: 1px solid transparent;
  border-radius: 16px;
  transition: all 0.3s ease;
  padding: 14px 0px 13px;

  .network-notice-title {
    background: linear-gradient(to bottom, #F9AF65, #F77B00);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-size: 40px;
    font-weight: 700;
    line-height: 1.2;
  }

  .network-notice-main {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #FFF;
    font-size: 12px;
    font-weight: 400;
  }
}

// 响应式
@media (max-width: 400px) {
  .notice-dialog-container {
    width: 90vw;
    max-width: 340px;
  }

  .network-notice-container {
    min-width: 280px;
    max-width: 340px;
    padding: 20px 30px;

    .network-notice-title {
      font-size: 36px;
    }

    .network-notice-main {
      font-size: 16px;
    }

    .network-notice-sub {
      font-size: 13px;
    }
  }
}
</style>
